import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Button, Input, Dropdown, Divider, Checkbox } from 'semantic-ui-react';
import i18next from 'i18next';
import { saveKiosk, editEntityField, setEditedEntity } from '../../../actions';
import { getCurrentRestaurant } from '../../../selectors';
import api from '../../../actions/api';
import { terminalConfigFactory } from '../../common/DataGrid/filters/helper';

const templateOptions = ['v1', 'v2', 'v3'];
const languageOptions = ['he', 'en', 'ar'];
const orderOptions = ['sitting', 'takeaway'];
const servingMethods = ['name', 'buzzer', 'table', 'counter', 'email', 'phone', 'phone-name', 'member', 'qr', 'name-buzzer', 'contact', 'none'];
const creditCardOptions = ['mastercard', 'visa', 'diners', 'amex', 'isracard', 'lc-max', 'maestro'];
const loyaltyProgramProviders = ['paidit', 'como', 'valuecard', 'verifone', 'verifone_gi', 'dreamcard', 'pizzahut'];
const cashOptions = ['pos', 'brinks'];
const paymentProviders = ['tenbis', 'cibus', 'goodi', 'multipass'];

class KioskEditor extends Component {
    handleSubmit = (event) => {
        this.props.saveKiosk(this.props.entity);
        event.preventDefault();
    }

    generateToken = () => {
        const { id, restaurantId } = this.props.entity;
        api.kiosks.generateToken(id, restaurantId).then(({ data }) => {
            const text = data.token;
            navigator.clipboard.writeText(text).then(function () {
                alert('Successful Copied to clipboard!\n' + text);
            }, function (err) {
                alert('Failed to copy!\n' + text);
            });
        });
    }

    renderTerminalConfig = terminalConfig => {
        if (terminalConfig && terminalConfig.terminalType) {
            const { config } = this.props.paymentTerminals.find(pt => pt.terminalType == terminalConfig.terminalType);

            return config.filter(({ field }) => !['terminalType', 'creditProviders'].includes(field)).map(({ field, type }) => {
                return (
                    <Form.Field key={field}>
                        {type === 'bool'
                            ? <Checkbox
                                label={field}
                                checked={terminalConfig[field]}
                                onChange={(e, { checked }) => this.props.editEntityField(`terminalConfig.${field}`, checked)}
                            />
                            : <Input
                                label={field}
                                placeholder={field}
                                value={terminalConfig[field] || ''}
                                onChange={({ target }) => {
                                    this.props.editEntityField(`terminalConfig.${field}`, target.value);
                                }}
                            />
                        }
                    </Form.Field>
                );
            });
        }

        return null;
    }

    render() {
        const { entity, paymentTerminals, flowTypes, themeOptions } = this.props;

        const kiosk = entity || {};

        const { plugins } = kiosk;

        return (
            <Form onSubmit={this.handleSubmit}>
                <Form.Field>
                    <Dropdown
                        placeholder={i18next.t('flowType')}
                        fluid
                        selection
                        options={flowTypes.map(value => ({ value, text: value }))}
                        value={kiosk.flow}
                        onChange={(e, { value }) => this.props.editEntityField('flow', value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Dropdown
                        fluid
                        selection
                        search
                        options={this.props.brands.map(value => ({ value, text: value }))}
                        value={kiosk.brand || ''}
                        onChange={(e, { value }) => this.props.editEntityField('brand', value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Dropdown
                        fluid
                        selection
                        options={servingMethods.map(value => ({ value, text: i18next.t('kiosks.servingMethods.' + value) }))}
                        value={kiosk.servingMethod || ''}
                        onChange={(e, { value }) => this.props.editEntityField('servingMethod', value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Dropdown
                        placeholder={i18next.t('general.languages')}
                        fluid
                        selection
                        multiple
                        options={languageOptions.map(value => ({ value, text: i18next.t('languages.' + value) }))}
                        value={kiosk.languages || []}
                        onChange={(e, { value }) => this.props.editEntityField('languages', value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Dropdown
                        placeholder={i18next.t('supportedOrderTypes')}
                        fluid
                        selection
                        multiple
                        options={orderOptions.map(value => ({ value, text: i18next.t(value) }))}
                        value={kiosk.orderOptions || []}
                        onChange={(e, { value }) => this.props.editEntityField('orderOptions', value)}
                    />
                </Form.Field>

                <Form.Group widths='equal'>
                    <Form.Field>
                        <Dropdown
                            required
                            fluid
                            selection
                            options={themeOptions.map(value => ({ value, text: value }))}
                            value={kiosk.theme || ''}
                            onChange={(e, { value }) => this.props.editEntityField('theme', value)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Dropdown
                            required
                            fluid
                            selection
                            options={templateOptions.map(value => ({ value, text: value }))}
                            value={kiosk.template || ''}
                            onChange={(e, { value }) => this.props.editEntityField('template', value)}
                        />
                    </Form.Field>
                </Form.Group>

                <Form.Field>
                    <Input
                        fluid
                        placeholder={i18next.t('distributor')}
                        value={kiosk.distributor || ''}
                        onChange={({ target }) => this.props.editEntityField('distributor', target.value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Input
                        fluid
                        placeholder={`${i18next.t('note')}...`}
                        value={kiosk.note || ''}
                        onChange={({ target }) => this.props.editEntityField('note', target.value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.terminalConfig')}
                        checked={!!kiosk.terminalConfig}
                        onChange={(e, { checked }) => this.props.editEntityField('terminalConfig', checked ? {} : null)}
                    />
                </Form.Field>

                {kiosk.terminalConfig &&
                    <Fragment>
                        <Form.Field>
                            <Dropdown
                                required
                                fluid
                                selection
                                options={paymentTerminals.map(terminal => ({ value: terminal.terminalType, text: terminal.terminalType }))}
                                value={kiosk.terminalConfig.terminalType || ''}
                                onChange={(e, { value }) => this.props.editEntityField('terminalConfig', terminalConfigFactory(value, paymentTerminals))}
                            />
                        </Form.Field>
                        {this.renderTerminalConfig(kiosk.terminalConfig)}

                        {String(kiosk.terminalConfig.terminalType).includes('verifone') && this.props.restaurant && this.props.restaurant.installmentsFormula &&
                            <Form.Checkbox
                                label={i18next.t('kiosks.installments')}
                                checked={!!kiosk.terminalConfig.allowInstallments}
                                onChange={(e, { checked }) => this.props.editEntityField('terminalConfig.allowInstallments', checked)}
                            />
                        }
                        <Form.Checkbox
                            label={i18next.t('kiosks.emvTerminal')}
                            checked={!!kiosk.terminalConfig.isEmv}
                            onChange={(e, { checked }) => this.props.editEntityField('terminalConfig.isEmv', checked)}
                        />
                        <Form.Group widths='equal'>
                            <Form.Field>
                                <Dropdown
                                    fluid
                                    selection
                                    multiple
                                    options={creditCardOptions.map(value => ({ value, text: value }))}
                                    value={kiosk.terminalConfig.creditProviders || []}
                                    onChange={(e, { value }) => this.props.editEntityField('terminalConfig.creditProviders', value.length ? value : undefined)}
                                />
                            </Form.Field>
                        </Form.Group>
                    </Fragment>
                }

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.cashConfig')}
                        checked={!!kiosk.cashConfig}
                        onChange={(e, { checked }) => this.props.editEntityField('cashConfig', checked ? { type: 'pos' } : null)}
                    />
                </Form.Field>

                {kiosk.cashConfig &&
                    <div>
                        <Form.Field>
                            <Dropdown
                                required
                                fluid
                                selection
                                options={cashOptions.map(value => ({ value, text: value }))}
                                value={kiosk.cashConfig.type || (kiosk.cashConfig.ip ? 'brinks' : 'pos')}
                                onChange={(e, { value }) => this.props.editEntityField('cashConfig', { type: value })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Input
                                fluid
                                placeholder={'Host'}
                                value={kiosk.cashConfig.ip || ''}
                                onChange={({ target }) => this.props.editEntityField('cashConfig.ip', target.value)}
                            />
                        </Form.Field>
                        <Form.Group widths='equal'>
                            <Form.Field>
                                <Input
                                    fluid
                                    placeholder={'User'}
                                    value={kiosk.cashConfig.user || ''}
                                    onChange={({ target }) => this.props.editEntityField('cashConfig.user', target.value)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Input
                                    fluid
                                    placeholder={'Password'}
                                    value={kiosk.cashConfig.password || ''}
                                    onChange={({ target }) => this.props.editEntityField('cashConfig.password', target.value)}
                                />
                            </Form.Field>
                        </Form.Group>
                    </div>
                }

                <Form.Field>
                    <Dropdown
                        placeholder={paymentProviders.join('/')}
                        fluid
                        selection
                        multiple
                        options={paymentProviders.map(value => ({ value, text: value }))}
                        value={kiosk.paymentProviders || []}
                        onChange={(e, { value }) => this.props.editEntityField('paymentProviders', value)}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.organizationConfig')}
                        checked={!!kiosk.organizationConfig}
                        onChange={(e, { checked }) => this.props.editEntityField('organizationConfig', checked ? {} : null)}
                    />
                </Form.Field>

                {kiosk.organizationConfig &&
                    <Form.Group widths='equal'>
                        <Form.Field>
                            <Input
                                fluid
                                placeholder={'Key'}
                                required
                                value={kiosk.organizationConfig.key || ''}
                                onChange={({ target }) => this.props.editEntityField('organizationConfig.key', target.value)}
                            />
                        </Form.Field>
                    </Form.Group>
                }

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.customerTypes')}
                        checked={Boolean(kiosk.customerTypes && kiosk.customerTypes.length)}
                        onChange={(e, { checked }) => this.props.editEntityField('customerTypes', checked ? ['employee'] : null)}
                    />
                </Form.Field>

                {kiosk.customerTypes &&
                    <Form.Field>
                        <Dropdown
                            required
                            fluid
                            selection
                            multiple
                            options={['employee'].map(value => ({ value, text: i18next.t(`customerTypes.${value}`) }))}
                            value={kiosk.customerTypes}
                            onChange={(e, { value }) => this.props.editEntityField('customerTypes', value.length ? value : null)}
                        />
                    </Form.Field>
                }

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.loyaltyProgramProvider')}
                        checked={!!kiosk.loyaltyProgramProvider}
                        onChange={(e, { checked }) => {
                            const membershipsProvider = checked ? 'paidit' : null;
                            this.props.editEntityField('loyaltyProgramProvider', membershipsProvider);
                            this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, membershipsProvider });
                        }}
                    />
                </Form.Field>

                {kiosk.loyaltyProgramProvider &&
                    <Form.Field>
                        <Dropdown
                            required
                            fluid
                            selection
                            options={loyaltyProgramProviders.map(value => ({ value, text: value }))}
                            value={kiosk.loyaltyProgramProvider || ''}
                            onChange={(e, { value }) => {
                                this.props.editEntityField('loyaltyProgramProvider', value);
                                this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, membershipsProvider: value });
                            }}
                        />
                    </Form.Field>
                }

                {kiosk.loyaltyProgramProvider &&
                    <Fragment>
                        <Form.Field>
                            <Dropdown
                                required
                                fluid
                                selection
                                options={['low', 'high'].map(value => ({ value, text: value }))}
                                value={kiosk.loyaltyProgramConfig && kiosk.loyaltyProgramConfig.promotionLevel || ''}
                                onChange={(e, { value }) => this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, promotionLevel: value })}
                            />
                        </Form.Field>
                        <Form.Checkbox
                            label={i18next.t('kiosks.allowRegistration')}
                            checked={kiosk.loyaltyProgramConfig && kiosk.loyaltyProgramConfig.allowRegistration}
                            onChange={(e, { checked }) => this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, allowRegistration: checked })}
                        />
                        <Form.Checkbox
                            label={i18next.t('kiosks.allowGlobalBenefits')}
                            checked={kiosk.loyaltyProgramConfig && kiosk.loyaltyProgramConfig.allowGlobalBenefits}
                            onChange={(e, { checked }) => this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, allowGlobalBenefits: ['dreamcard', 'verifone'].includes(kiosk.loyaltyProgramProvider) && checked })}
                        />
                        <Form.Checkbox
                            label={i18next.t('kiosks.allowCreditsPicker')}
                            checked={kiosk.loyaltyProgramConfig && kiosk.loyaltyProgramConfig.allowCreditsPicker}
                            onChange={(e, { checked }) => this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, allowCreditsPicker: checked })}
                        />
                        <Divider />
                    </Fragment>
                }

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.couponsProvider')}
                        checked={!!kiosk.couponsProvider}
                        onChange={(e, { checked }) => {
                            const couponsProvider = checked ? 'paidit' : null;
                            this.props.editEntityField('couponsProvider', couponsProvider);
                            this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, couponsProvider });
                        }}
                    />
                </Form.Field>

                {kiosk.couponsProvider &&
                    <Form.Field>
                        <Dropdown
                            required
                            fluid
                            selection
                            options={loyaltyProgramProviders.map(value => ({ value, text: value }))}
                            value={kiosk.couponsProvider || ''}
                            onChange={(e, { value }) => {
                                this.props.editEntityField('couponsProvider', value);
                                this.props.editEntityField('loyaltyProgramConfig', { ...kiosk.loyaltyProgramConfig, couponsProvider: value });
                            }}
                        />
                    </Form.Field>
                }

                {kiosk.flow === 'itemScan' &&
                    <Form.Field>
                        <label>{i18next.t('plugins')}</label>
                        <Dropdown
                            fluid
                            selection
                            multiple
                            options={['smartTags', 'replacementVoucher', 'manualBarcodeSetup'].map(value => ({ value, text: value }))}
                            value={Object.keys(plugins || {})}
                            onChange={(e, { value }) => {
                                this.props.editEntityField(
                                    'plugins',
                                    value.reduce((plugins, key) => {
                                        if (key === 'smartTags') {
                                            plugins.smartTags = { ...(kiosk.plugins || {}).smartTags, mode: 'hard', provider: 'sensormatic' };
                                        }
                                        if (key === 'manualBarcodeSetup') {
                                            plugins.manualBarcodeSetup = {};
                                        }
                                        if (key === 'manualNumericOnlyBarcode') {
                                            plugins.manualBarcodeSetup = { numericOnly: true };
                                        }
                                        if (key === 'replacementVoucher') {
                                            plugins.replacementVoucher = true;
                                        }
                                        return plugins;
                                    }, {})
                                );
                            }}
                        />
                    </Form.Field>
                }

                {(plugins || {}).smartTags &&
                    <>
                        <Form.Field>
                            <Form.Checkbox
                                label={'External scanner'}
                                checked={!!plugins.smartTags.externalScanner}
                                onChange={(e, { checked }) => this.props.editEntityField('plugins', {
                                    ...plugins,
                                    smartTags: { ...plugins.smartTags, externalScanner: checked }
                                })}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Input
                                fluid
                                placeholder={'URL'}
                                value={plugins.smartTags.url || ''}
                                onChange={({ target }) => this.props.editEntityField('plugins', {
                                    ...plugins,
                                    smartTags: { ...plugins.smartTags, url: target.value }
                                })}
                            />
                        </Form.Field>
                    </>
                }

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('kiosks.hasPrinter')}
                        checked={kiosk.hasPrinter}
                        onChange={(e, { checked }) => this.props.editEntityField('hasPrinter', checked)}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('supportMinimizeMode')}
                        checked={kiosk.supportMinimizeMode}
                        onChange={(e, { checked }) => this.props.editEntityField('supportMinimizeMode', checked)}
                    />
                </Form.Field>

                <Form.Field>
                    <Form.Checkbox
                        label={i18next.t('suspended')}
                        checked={kiosk.isDisabled}
                        onChange={(e, { checked }) => this.props.editEntityField('isDisabled', checked)}
                    />
                </Form.Field>

                <Button primary type='submit' floated="right">
                    {i18next.t('save')}
                </Button>

                {kiosk.id &&
                    <Button secondary type='button' floated="left" onClick={this.generateToken}>
                        {'Generate Token'}
                    </Button>
                }
            </Form>
        );
    }
}

KioskEditor.propTypes = {
    saveKiosk: PropTypes.func,
    editEntityField: PropTypes.func,
    setEditedEntity: PropTypes.func,
    brands: PropTypes.array,
    flowTypes: PropTypes.array,
    themeOptions: PropTypes.array,
    paymentTerminals: PropTypes.array,
    entity: PropTypes.object,
    restaurant: PropTypes.object,
};

const mapStateToProps = (state) => {
    const { entity } = state.editor;
    const { paymentTerminals, flowTypes, themeOptions } = state.meta;
    const brands = state.businessApp.brands.map(b => b.key);
    const restaurant = getCurrentRestaurant(state);

    return { entity, restaurant, brands, paymentTerminals, flowTypes, themeOptions };
};

export default connect(mapStateToProps, {
    saveKiosk,
    editEntityField,
    setEditedEntity,
})(KioskEditor);
